/**
 * Readiness BI tab — Phase 3 (Agent Readiness Score PRD §6).
 * Batch score-over-time, TTP percentiles, cohort comparison, backfill replay.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Gauge, BadgeCheck, Timer, TrendingUp, History, Boxes } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { buildQS } from './BIFilterBar';

const LINE_COLORS = ['#b68235', '#7d7979', '#7d5411', '#e1ad66', '#444141', '#c28d41', '#9b9797', '#5a3b0a'];
const STATE_META = {
  floor_ready: { label: 'Floor ready', color: '#10B981' },
  on_track:    { label: 'On track',    color: '#b68235' },
  at_risk:     { label: 'At risk',     color: '#F59E0B' },
  not_ready:   { label: 'Not ready',   color: '#F43F5E' },
  not_scored:  { label: 'Not scored',  color: '#94A3B8' },
};

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <Card className="p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg bg-[var(--lumyn-analytics,#b68235)]/10 text-[var(--lumyn-analytics,#b68235)] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-xl font-bold font-display leading-none tabular-nums">{value}</p>
        <p className="text-xs text-muted-foreground mt-1">{label}</p>
      </div>
    </Card>
  );
}

const Empty = ({ msg = 'No data in this range.' }) => (
  <p className="text-sm text-muted-foreground py-6 text-center">{msg}</p>
);

export default function ReadinessBITab({ filters }) {
  const { get, post } = useApi();
  const { hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    const qs = buildQS(filters);
    const sep = qs ? '&' : '?';
    get(`/analytics/bi/readiness${qs}${sep}include_completed=${includeCompleted}`)
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [get, filters, includeCompleted, refreshKey]);

  const runBackfill = useCallback(async () => {
    setBackfilling(true);
    try {
      const r = await post('/readiness/backfill', {});
      const done = (r.results || []).filter(x => !x.skipped && !x.error).length;
      toast.success(`History replay complete — ${done} batch(es) reconstructed`);
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(e?.message || 'Backfill failed');
    } finally { setBackfilling(false); }
  }, [post]);

  const chart = useMemo(() => {
    if (!data) return { rows: [], batches: [] };
    const byDate = {};
    (data.batch_trends || []).forEach(t => {
      t.series.forEach(p => {
        if (p.avg_score == null) return;
        byDate[p.date] = byDate[p.date] || { date: p.date };
        byDate[p.date][t.name] = p.avg_score;
      });
    });
    return {
      rows: Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)),
      batches: data.batch_trends || [],
    };
  }, [data]);

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const cur = data.current || {};
  const pie = Object.entries(cur.state_counts || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: STATE_META[k]?.label || k, value: v, color: STATE_META[k]?.color || '#94A3B8' }));
  const anyRecon = (data.batch_trends || []).some(t => t.reconstructed)
    || (data.ttp || []).some(r => r.reconstructed);

  return (
    <div className="space-y-4" data-testid="bi-readiness-tab">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm" data-testid="readiness-include-completed">
          <Switch checked={includeCompleted} onCheckedChange={setIncludeCompleted} />
          Include completed batches
        </label>
        <div className="flex-1" />
        {hasPermission('readiness.recompute') && (
          <Button variant="outline" size="sm" onClick={runBackfill} disabled={backfilling}
                  data-testid="readiness-backfill-btn">
            <History className="w-3.5 h-3.5 mr-1.5" />
            {backfilling ? 'Replaying…' : 'Rebuild history (weekly replay)'}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Kpi icon={Gauge} label="Avg readiness" value={cur.avg_score != null ? cur.avg_score : '—'} testid="rkpi-avg" />
        <Kpi icon={BadgeCheck} label="Floor ready" value={`${cur.floor_ready || 0} / ${cur.n_trainees || 0}`} testid="rkpi-ready" />
        <Kpi icon={Timer} label="Median days to ready" value={cur.median_days_to_ready != null ? cur.median_days_to_ready : '—'} testid="rkpi-ttp" />
        <Kpi icon={TrendingUp} label="With projection" value={cur.with_projection || 0} testid="rkpi-projection" />
        <Kpi icon={Boxes} label="Batches tracked" value={(data.ttp || []).length} testid="rkpi-batches" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-2" data-testid="readiness-trend-chart">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-display text-sm font-semibold">Batch readiness over time</h4>
            {anyRecon && (
              <Badge variant="outline" className="text-[10px]">dashed = reconstructed history</Badge>
            )}
          </div>
          {chart.rows.length === 0 ? (
            <Empty msg="No history yet — run a recompute or the weekly replay to build trends." />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chart.rows}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={x => x.slice(5)} fontSize={10} />
                <YAxis domain={[0, 100]} fontSize={10} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {chart.batches.map((t, i) => (
                  <Line key={t.batch_id} type="monotone" dataKey={t.name} connectNulls
                        stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={2}
                        strokeDasharray={t.reconstructed ? '6 4' : undefined}
                        dot={{ r: 2.5 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card className="p-4" data-testid="readiness-state-pie">
          <h4 className="font-display text-sm font-semibold mb-2">Current state distribution</h4>
          {pie.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pie} dataKey="value" innerRadius={50} outerRadius={78} paddingAngle={3}>
                  {pie.map(p => <Cell key={p.name} fill={p.color} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <Card className="p-4" data-testid="readiness-ttp-table">
        <h4 className="font-display text-sm font-semibold mb-1">Time to proficiency (days from batch start to first floor-ready)</h4>
        <p className="text-xs text-muted-foreground mb-2">First-crossing semantics — a trainee's days-to-ready never moves even if the score later dips.</p>
        {(data.ttp || []).length === 0 ? <Empty msg="No batches in scope." /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Batch</TableHead><TableHead className="w-20">Status</TableHead>
                <TableHead className="w-24">Ready / Active</TableHead>
                <TableHead className="w-20">Ready %</TableHead>
                <TableHead className="w-28">Median days</TableHead>
                <TableHead className="w-28">P25–P75</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.ttp.map(r => (
                <TableRow key={r.batch_id} data-testid={`ttp-row-${r.batch_id}`}>
                  <TableCell className="font-medium text-sm">
                    {r.name}
                    {r.reconstructed && <Badge variant="outline" className="ml-1.5 text-[9px]">reconstructed</Badge>}
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{r.status}</Badge></TableCell>
                  <TableCell className="tabular-nums">{r.n_ready} / {r.n_active}</TableCell>
                  <TableCell className="tabular-nums">{r.pct_ready != null ? `${r.pct_ready}%` : '—'}</TableCell>
                  <TableCell className="tabular-nums font-semibold">{r.median_days ?? '—'}</TableCell>
                  <TableCell className="tabular-nums text-xs text-muted-foreground">
                    {r.p25_days != null ? `${r.p25_days} – ${r.p75_days}` : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4" data-testid="readiness-dept-cohorts">
          <h4 className="font-display text-sm font-semibold mb-2">Cohort comparison — by department</h4>
          {(data.cohorts?.by_department || []).length === 0 ? <Empty /> : (
            <>
              <ResponsiveContainer width="100%" height={Math.max(120, data.cohorts.by_department.length * 42)}>
                <BarChart data={data.cohorts.by_department} layout="vertical">
                  <XAxis type="number" domain={[0, 100]} fontSize={10} />
                  <YAxis type="category" dataKey="name" width={130} fontSize={10} />
                  <Tooltip />
                  <Bar dataKey="avg_score" name="Avg readiness" fill="#b68235" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Department</TableHead><TableHead className="w-20">Trainees</TableHead>
                    <TableHead className="w-20">Ready %</TableHead><TableHead className="w-24">Median days</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.cohorts.by_department.map(r => (
                    <TableRow key={r.department_id || 'none'}>
                      <TableCell className="text-sm font-medium">{r.name}</TableCell>
                      <TableCell>{r.trainees}</TableCell>
                      <TableCell>{r.pct_ready != null ? `${r.pct_ready}%` : '—'}</TableCell>
                      <TableCell>{r.median_days ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Card>
        <Card className="p-4" data-testid="readiness-quarter-cohorts">
          <h4 className="font-display text-sm font-semibold mb-2">Cohort comparison — by start quarter</h4>
          {(data.cohorts?.by_quarter || []).length === 0 ? <Empty /> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quarter</TableHead><TableHead className="w-20">Batches</TableHead>
                  <TableHead className="w-20">Trainees</TableHead>
                  <TableHead className="w-24">Avg readiness</TableHead>
                  <TableHead className="w-20">Ready %</TableHead>
                  <TableHead className="w-24">Median days</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.cohorts.by_quarter.map(r => (
                  <TableRow key={r.quarter}>
                    <TableCell className="text-sm font-medium">{r.quarter}</TableCell>
                    <TableCell>{r.batches}</TableCell>
                    <TableCell>{r.trainees}</TableCell>
                    <TableCell>{r.avg_score ?? '—'}</TableCell>
                    <TableCell>{r.pct_ready != null ? `${r.pct_ready}%` : '—'}</TableCell>
                    <TableCell>{r.median_days ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
}
